import { useMemo } from 'react';

import { CheckboxProps, RadioProps } from '@innovationnorway/cl-react';
import { CompanyAgeCriteria, CompanySizeCriteria, CustomerSegmentTargetGroup } from 'src/api/v2';
import { useTranslation } from 'react-i18next';

const useTargetPageOptions = (
  selectedCompanyAgeOption?: string,
  selectedCompanySizeOptions?: string[],
  selectedCompanyTypeOptions?: string[],
) => {
  const { t } = useTranslation('oneWayIn');
  const companyAgeOptions: RadioProps[] = useMemo<RadioProps[]>(
    () => [
      {
        value: CompanyAgeCriteria.RelevantForAllAges,
        label: t('companyAge.RelevantForAllAges'),
        checked: selectedCompanyAgeOption === CompanyAgeCriteria.RelevantForAllAges,
      },
      {
        value: CompanyAgeCriteria.UnderThreeYears,
        label: t('companyAge.UnderThreeYears'),
        checked: selectedCompanyAgeOption === CompanyAgeCriteria.UnderThreeYears,
      },
      {
        value: CompanyAgeCriteria.UnderFiveYears,
        label: t('companyAge.UnderFiveYears'),
        checked: selectedCompanyAgeOption === CompanyAgeCriteria.UnderFiveYears,
      },
      {
        value: CompanyAgeCriteria.OverFiveYears,
        label: t('companyAge.OverFiveYears'),
        checked: selectedCompanyAgeOption === CompanyAgeCriteria.OverFiveYears,
      },
    ],
    [selectedCompanyAgeOption, t],
  );

  const companySizeOptions: CheckboxProps[] = useMemo<CheckboxProps[]>(
    () => [
      {
        value: CompanySizeCriteria.Small,
        label: t('companySize.Small'),
        description: t('companySize.Small.Description'),
        checked: selectedCompanySizeOptions?.includes(CompanySizeCriteria.Small) ?? false,
      },
      {
        value: CompanySizeCriteria.Medium,
        label: t('companySize.Medium'),
        description: t('companySize.Medium.Description'),
        checked: selectedCompanySizeOptions?.includes(CompanySizeCriteria.Medium) ?? false,
      },
      {
        value: CompanySizeCriteria.Large,
        label: t('companySize.Large'),
        description: t('companySize.Large.Description'),
        checked: selectedCompanySizeOptions?.includes(CompanySizeCriteria.Large) ?? false,
      },
      {
        value: CompanySizeCriteria.OtherCategoriesMayApply,
        label: t('companySize.OtherCategoriesMayApply'),
        checked: selectedCompanySizeOptions?.includes('no') ?? false,
      },
    ],
    [selectedCompanySizeOptions, t],
  );

  const customerSegmentOptions: CheckboxProps[] = useMemo<CheckboxProps[]>(
    () => [
      {
        value: CustomerSegmentTargetGroup.OnlyForPublicEntities,
        label: 'Kun for offentlige aktører',
        checked:
          selectedCompanyTypeOptions?.includes(CustomerSegmentTargetGroup.OnlyForPublicEntities) ?? false,
      },
      {
        value: CustomerSegmentTargetGroup.MustOwnAgriculturalProperty,
        label: 'Må ha landbrukseiendom',
        checked:
          selectedCompanyTypeOptions?.includes(CustomerSegmentTargetGroup.MustOwnAgriculturalProperty) ??
          false,
      },
      {
        value: CustomerSegmentTargetGroup.RequiresParticipationInACluster,
        label: 'Må være en del av en klynge',
        checked:
          selectedCompanyTypeOptions?.includes(CustomerSegmentTargetGroup.RequiresParticipationInACluster) ??
          false,
      },
      {
        value: CustomerSegmentTargetGroup.OnlyForTourismIndustry,
        label: 'Må være en del av reiselivsnæringen',
        checked:
          selectedCompanyTypeOptions?.includes(CustomerSegmentTargetGroup.OnlyForTourismIndustry) ?? false,
      },
    ],
    [selectedCompanyTypeOptions],
  );

  return {
    companyAgeOptions,
    companySizeOptions,
    customerSegmentOptions,
  };
};

export default useTargetPageOptions;
